(function exposeTitanScanEffects(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.TitanScanEffects = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTitanScanEffects() {
  'use strict';

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function createSoundController(initialMuted = true) {
    let muted = initialMuted !== false;
    let context = null;
    let available = true;

    function getContext() {
      if (!available) return null;
      if (context) return context;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        available = false;
        return null;
      }

      try {
        context = new AudioContext();
        return context;
      } catch (error) {
        available = false;
        return null;
      }
    }

    async function activate() {
      const audioContext = getContext();
      if (!audioContext) return false;

      try {
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        return audioContext.state === 'running';
      } catch (error) {
        available = false;
        return false;
      }
    }

    function playTone(frequency, duration, options = {}) {
      if (muted || !available) return false;
      const audioContext = getContext();
      if (!audioContext || audioContext.state !== 'running') return false;

      try {
        const start = audioContext.currentTime + (options.delay || 0);
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        oscillator.type = options.type || 'sine';
        oscillator.frequency.setValueAtTime(frequency, start);
        if (options.endFrequency) {
          oscillator.frequency.exponentialRampToValueAtTime(options.endFrequency, start + duration);
        }
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(options.volume || 0.045, start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start(start);
        oscillator.stop(start + duration + 0.02);
        return true;
      } catch (error) {
        available = false;
        return false;
      }
    }

    function play(name) {
      if (name === 'hit') {
        return playTone(560, 0.055, { type: 'square', volume: 0.025, endFrequency: 880 });
      }
      if (name === 'ready') {
        return playTone(720, 0.12, { type: 'sine', volume: 0.05, endFrequency: 1080 });
      }
      if (name === 'early') {
        return playTone(150, 0.14, { type: 'sawtooth', volume: 0.025, endFrequency: 90 });
      }
      if (name === 'complete') {
        const first = playTone(520, 0.14, { type: 'triangle', volume: 0.04, endFrequency: 680 });
        const second = playTone(780, 0.18, {
          type: 'triangle',
          volume: 0.035,
          delay: 0.11,
          endFrequency: 1040,
        });
        return first || second;
      }
      return false;
    }

    async function setMuted(nextMuted) {
      muted = nextMuted !== false;
      if (!muted) {
        await activate();
      }
      return { muted, available };
    }

    return Object.freeze({
      setMuted,
      play,
      getState: () => ({ muted, available }),
    });
  }

  function spawnHitEffect(container, localX, localY, reducedMotion) {
    if (!container || reducedMotion) return;
    const effect = document.createElement('span');
    effect.className = 'hit-effect';
    effect.style.setProperty('--hit-x', `${localX}px`);
    effect.style.setProperty('--hit-y', `${localY}px`);
    effect.setAttribute('aria-hidden', 'true');
    container.append(effect);
    window.setTimeout(() => effect.remove(), 240);
  }

  function animateNumber(element, value, options = {}) {
    if (!element) return () => {};
    const target = Number.isFinite(Number(value)) ? Number(value) : 0;
    const format = typeof options.format === 'function' ? options.format : Math.round;
    const duration = options.reducedMotion ? 0 : Math.max(0, options.duration || 700);
    let frameId = null;
    let cancelled = false;

    if (duration === 0) {
      element.textContent = String(format(target));
      return () => {};
    }

    const start = performance.now();
    const tick = (now) => {
      if (cancelled) return;
      const progress = clamp((now - start) / duration, 0, 1);
      const eased = 1 - (1 - progress) ** 3;
      element.textContent = String(format(target * eased));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }

  function createRadarRenderer(canvas) {
    if (!canvas || !canvas.getContext) {
      return Object.freeze({ render: () => {}, cancel: () => {} });
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return Object.freeze({ render: () => {}, cancel: () => {} });
    }
    let frameId = null;

    function cancel() {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    }

    function resize() {
      const cssSize = clamp(canvas.parentElement ? canvas.parentElement.clientWidth : 500, 260, 500);
      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      canvas.style.width = `${cssSize}px`;
      canvas.style.height = `${cssSize}px`;
      canvas.width = Math.round(cssSize * dpr);
      canvas.height = Math.round(cssSize * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      return cssSize;
    }

    function point(centerX, centerY, radius, index, count) {
      const angle = -Math.PI / 2 + (index * Math.PI * 2) / count;
      return {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      };
    }

    function polygon(centerX, centerY, radius, count) {
      context.beginPath();
      for (let index = 0; index < count; index += 1) {
        const current = point(centerX, centerY, radius, index, count);
        if (index === 0) context.moveTo(current.x, current.y);
        else context.lineTo(current.x, current.y);
      }
      context.closePath();
    }

    function draw(values, labels, progress, size) {
      const center = size / 2;
      const radius = size * 0.31;
      const count = 5;
      context.clearRect(0, 0, size, size);

      context.lineWidth = 1;
      for (let level = 1; level <= 5; level += 1) {
        polygon(center, center, (radius * level) / 5, count);
        context.strokeStyle = 'rgba(18, 18, 18, 0.24)';
        context.stroke();
      }

      for (let index = 0; index < count; index += 1) {
        const outer = point(center, center, radius, index, count);
        context.beginPath();
        context.moveTo(center, center);
        context.lineTo(outer.x, outer.y);
        context.strokeStyle = 'rgba(18, 18, 18, 0.2)';
        context.stroke();
      }

      context.beginPath();
      values.slice(0, count).forEach((value, index) => {
        const current = point(
          center,
          center,
          radius * clamp(Number(value) / 100, 0, 1) * progress,
          index,
          count,
        );
        if (index === 0) context.moveTo(current.x, current.y);
        else context.lineTo(current.x, current.y);
      });
      context.closePath();
      context.fillStyle = 'rgba(51, 79, 180, 0.16)';
      context.strokeStyle = '#334fb4';
      context.lineWidth = 2;
      context.fill();
      context.stroke();

      labels.slice(0, count).forEach((label, index) => {
        const labelPoint = point(center, center, radius + size * 0.105, index, count);
        context.fillStyle = '#606673';
        context.font = `700 ${Math.max(11, size * 0.027)}px sans-serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(label, labelPoint.x, labelPoint.y);
      });
    }

    function render(values, labels, reducedMotion) {
      cancel();
      const size = resize();
      if (reducedMotion) {
        draw(values, labels, 1, size);
        return;
      }

      const startedAt = performance.now();
      const tick = (now) => {
        const progress = clamp((now - startedAt) / 720, 0, 1);
        draw(values, labels, 1 - (1 - progress) ** 3, size);
        if (progress < 1) frameId = requestAnimationFrame(tick);
        else frameId = null;
      };
      frameId = requestAnimationFrame(tick);
    }

    return Object.freeze({ render, cancel });
  }

  function drawGrid(context, width, height) {
    context.save();
    context.strokeStyle = 'rgba(244, 246, 242, 0.055)';
    context.lineWidth = 1;
    for (let x = 40; x < width; x += 56) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }
    for (let y = 40; y < height; y += 56) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }
    context.restore();
  }

  function fitText(context, text, maxWidth, initialSize, font) {
    let size = initialSize;
    do {
      context.font = `${font.weight} ${size}px ${font.family}`;
      if (context.measureText(text).width <= maxWidth) return size;
      size -= 2;
    } while (size > 22);
    return size;
  }

  async function waitForFonts() {
    if (!document.fonts || !document.fonts.ready) return;
    await Promise.race([
      document.fonts.ready,
      new Promise((resolve) => window.setTimeout(resolve, 1200)),
    ]);
  }

  function buildShareText(result) {
    if (!result) throw new TypeError('A diagnosis result is required for sharing.');
    const lines = [
      'TITAN PLAYER SCAN',
      `${result.playerClass} / ${result.playerType}`,
      `${result.inputLabel} ${result.inputScore}・REACTION ${result.reactionAverage}ms・ACCURACY ${result.accuracy}%`,
    ];
    if (result.recommendation && result.recommendation.gpu) {
      lines.push(`RECOMMENDED GPU ${result.recommendation.gpu}`);
    }
    return lines.join('\n');
  }

  function publicShareUrl(value) {
    if (typeof value !== 'string' || !value || value.startsWith('#')) return '';
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:') return '';
      if (url.searchParams.has('preview_theme_id') || url.searchParams.has('pb')) return '';
      return url.href;
    } catch (error) {
      return '';
    }
  }

  function buildSocialShareUrl(destination, result, shareUrl = '') {
    const endpoints = {
      x: 'https://x.com/intent/tweet',
      line: 'https://social-plugins.line.me/lineit/share',
    };
    const endpoint = endpoints[destination];
    if (!endpoint) throw new TypeError('Unsupported share destination.');
    const target = new URL(endpoint);
    target.searchParams.set('text', buildShareText(result));
    const publicUrl = publicShareUrl(shareUrl);
    if (publicUrl) target.searchParams.set('url', publicUrl);
    return target.href;
  }

  async function createShareCard(canvas, result) {
    if (!canvas || !canvas.getContext || !result) {
      throw new TypeError('Share card requires a canvas and diagnosis result.');
    }

    await waitForFonts();
    const context = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    context.clearRect(0, 0, width, height);
    context.fillStyle = '#050708';
    context.fillRect(0, 0, width, height);
    drawGrid(context, width, height);

    context.fillStyle = '#334fb4';
    context.fillRect(64, 54, 52, 52);
    context.fillStyle = '#f4f6f2';
    context.font = '800 34px "Barlow Condensed", sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText('T', 90, 80);

    context.textAlign = 'left';
    context.fillStyle = '#f4f6f2';
    context.font = '800 30px "Barlow Condensed", sans-serif';
    context.fillText('TITAN PLAYER SCAN', 136, 74);
    context.fillStyle = '#8e9aa1';
    context.font = '600 18px "Barlow Condensed", sans-serif';
    context.fillText('TITAN GAMING', 136, 101);

    context.fillStyle = '#8e9aa1';
    context.font = '600 20px "Barlow Condensed", sans-serif';
    context.fillText('PLAYER CLASS', 64, 172);
    const classSize = fitText(
      context,
      result.playerClass,
      550,
      118,
      { weight: 800, family: '"Barlow Condensed", sans-serif' },
    );
    context.fillStyle = '#f4f6f2';
    context.font = `800 ${classSize}px "Barlow Condensed", sans-serif`;
    context.fillText(result.playerClass, 64, 270);

    context.fillStyle = '#f4f6f2';
    context.font = '700 34px "Barlow Condensed", sans-serif';
    context.fillText(result.playerType, 66, 325);

    const metricX = [690, 875, 1040];
    const metricLabels = [result.inputLabel, 'REACTION', 'ACCURACY'];
    const metricValues = [result.inputScore, result.reactionAverage, result.accuracy];
    const metricUnits = ['/100', 'ms', '%'];
    metricLabels.forEach((label, index) => {
      context.fillStyle = '#8e9aa1';
      context.font = '600 17px "Barlow Condensed", sans-serif';
      context.fillText(label, metricX[index], 190);
      context.fillStyle = '#f4f6f2';
      context.font = '800 54px "Barlow Condensed", sans-serif';
      context.fillText(String(metricValues[index]), metricX[index], 250);
      context.fillStyle = '#8e9aa1';
      context.font = '600 16px "Barlow Condensed", sans-serif';
      context.fillText(metricUnits[index], metricX[index], 278);
    });

    context.fillStyle = '#334fb4';
    context.fillRect(64, 386, 1072, 3);
    context.fillStyle = '#8e9aa1';
    context.font = '600 19px "Barlow Condensed", sans-serif';
    context.fillText('RECOMMENDED GPU', 64, 438);
    fitText(
      context,
      result.recommendation.gpu,
      1000,
      52,
      { weight: 700, family: '"Murecho", sans-serif' },
    );
    context.fillStyle = '#f4f6f2';
    context.fillText(result.recommendation.gpu, 64, 492);

    context.fillStyle = '#8e9aa1';
    context.font = '500 22px "Murecho", sans-serif';
    context.fillText(
      `${result.gameName} / ${result.deviceLabel}`,
      64,
      548,
    );
    context.fillStyle = '#f4f6f2';
    context.font = '700 21px "Barlow Condensed", sans-serif';
    context.textAlign = 'right';
    context.fillText('TITAN-PC.NET', 1136, 548);
    context.fillStyle = '#59636a';
    context.font = '500 15px "Murecho", sans-serif';
    context.fillText('測定値はブラウザ上の簡易テスト結果です', 1136, 580);

    return canvas;
  }

  function dataUrlToBlob(dataUrl) {
    const encoded = dataUrl.split(',')[1];
    if (!encoded) throw new Error('Canvas image data is invalid.');
    const binary = window.atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: 'image/png' });
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      if (typeof canvas.toBlob !== 'function') {
        try {
          resolve(dataUrlToBlob(canvas.toDataURL('image/png')));
        } catch (error) {
          reject(error);
        }
        return;
      }
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not create the result image.'));
      }, 'image/png');
    });
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function canNativeShare() {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  }

  let preparedShareCard = null;
  function shareCardKey(result) {
    return [buildShareText(result), result.gameName || '', result.deviceLabel || ''].join('\u001f');
  }

  function prepareShareCard(canvas, result) {
    const key = shareCardKey(result);
    if (preparedShareCard && preparedShareCard.key === key) return preparedShareCard.promise;
    const promise = createShareCard(canvas, result)
      .then(() => canvasToBlob(canvas))
      .then((blob) => {
        if (preparedShareCard && preparedShareCard.key === key) {
          preparedShareCard = Object.freeze({ key, blob, promise: Promise.resolve(blob) });
        }
        return blob;
      })
      .catch((error) => {
        if (preparedShareCard && preparedShareCard.key === key) preparedShareCard = null;
        throw error;
      });
    preparedShareCard = Object.freeze({ key, blob: null, promise });
    return promise;
  }

  function preparedShareBlob(result) {
    const key = shareCardKey(result);
    return preparedShareCard && preparedShareCard.key === key ? preparedShareCard.blob : null;
  }

  async function downloadShareCard(canvas, result) {
    const blob = preparedShareBlob(result) || await prepareShareCard(canvas, result);
    downloadBlob(blob, 'titan-player-scan.png');
    return 'downloaded';
  }

  async function nativeShareCard(canvas, result, shareUrl) {
    if (!canNativeShare()) return 'unsupported';
    const blob = preparedShareBlob(result);
    if (!blob) return 'preparing';
    const shareData = {
      title: `TITAN PLAYER SCAN — ${result.playerClass}`,
      text: buildShareText(result),
    };
    const publicUrl = publicShareUrl(shareUrl);
    if (publicUrl) shareData.url = publicUrl;

    let includesFile = false;
    if (typeof File === 'function' && typeof navigator.canShare === 'function') {
      const file = new File([blob], 'titan-player-scan.png', { type: 'image/png' });
      try {
        if (navigator.canShare({ files: [file] })) {
          shareData.files = [file];
          includesFile = true;
        }
      } catch (error) {
        includesFile = false;
      }
    }

    try {
      await navigator.share(shareData);
      return includesFile ? 'shared-file' : 'shared-text';
    } catch (error) {
      if (error && error.name === 'AbortError') return 'cancelled';
      return 'failed';
    }
  }

  async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // Continue to the browser-compatible fallback.
      }
    }

    const previousFocus = document.activeElement;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    textarea.remove();
    if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
    return copied;
  }

  return Object.freeze({
    createSoundController,
    spawnHitEffect,
    animateNumber,
    createRadarRenderer,
    createShareCard,
    buildShareText,
    buildSocialShareUrl,
    canNativeShare,
    prepareShareCard,
    downloadShareCard,
    nativeShareCard,
    copyText,
  });
});
